import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { useDeleteSemen, useSemen, useUpdateSemen } from '../contexts/semen';
import { validateSemen } from '../utils/validate';
import { buildSemenPayload } from '../utils/payload';
import SemenFields from './fields';

import '../../../common/styles/catalog.css';

function buildForm(s) {
    return {
        code:     s.code || '',
        name:     s.name || '',
        race_id:  s.race_id != null ? String(s.race_id) : '',
        origin:   s.origin || '',
        supplier: s.supplier || '',
        codeTouched: true, nameTouched: true, raceTouched: true, // ya tienen valor → no re-derivar al cambiar el toro
        sireMode: 'existing', // el PATCH solo linkea por id; alta anidada es solo del POST
        sireAnimal: s.sire_animal_id != null
            ? { id: s.sire_animal_id, name: s.sire_name || `Animal #${s.sire_animal_id}`, number: '', register: s.sire_register || '' }
            : null,
        newName: '', newRegister: '', newRace: '',
        status:   s.status ?? 2,
    };
}

export default function EditSemenForm({ semenId }) {
    const navigate = useNavigate();
    const { data: semen, isLoading, error: loadError } = useSemen(semenId);
    const { mutateAsync: updateSemen, isPending: updating } = useUpdateSemen(semenId);
    const { mutateAsync: deleteSemen, isPending: deleting } = useDeleteSemen();
    const confirm = useConfirm();

    const [form, setForm]           = useState(null);
    const [seededFor, setSeededFor] = useState(null);
    const [localErrors, setLocalErrors]   = useState({});
    const [serverFields, setServerFields] = useState({});

    if (semen && seededFor !== semen.id) {
        setSeededFor(semen.id);
        setForm(buildForm(semen));
    }

    const setField = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (localErrors[field] || serverFields[field]) {
            setLocalErrors(prev => ({ ...prev, [field]: undefined }));
            setServerFields(prev => ({ ...prev, [field]: undefined }));
        }
    };
    const fieldError = (field) => localErrors[field] || serverFields[field];

    if (isLoading || (!form && !loadError)) return <div className="cat-form__state">Cargando semen…</div>;
    if (loadError || !semen) return <div className="cat-form__state">Semen no encontrado.</div>;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateSemen(form);
        if (Object.keys(errors).length) { setLocalErrors(errors); toast.error('Revisá los campos marcados'); return; }
        setLocalErrors({});
        setServerFields({});

        try {
            await updateSemen({ ...buildSemenPayload(form), status: form.status });
            toast.success('Semen actualizado');
            navigate(`/admin/semen/${semenId}`);
        } catch (err) {
            if (err?.status === 409) toast.error(err?.message || 'El código de semen ya existe');
            else if (err?.fields) { setServerFields(err.fields); toast.error('Revisá los campos marcados'); }
            else if (err?.status === 422) toast.error(err?.message || 'Elegí el toro (padre)');
            else toast.error(err?.message || 'No se pudo actualizar el semen');
        }
    };

    const handleDelete = async () => {
        const ok = await confirm({
            title: 'Eliminar semen',
            message: `¿Eliminar el semen "${semen.code}"?`,
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try {
            await deleteSemen(semenId);
            toast.success('Semen eliminado');
            navigate('/admin/semen');
        } catch (err) {
            if (err?.status === 409) toast.error('No se puede eliminar: tiene registros asociados');
            else toast.error(err?.message || 'No se pudo eliminar el semen');
        }
    };

    return (
        <form className="cat-form" onSubmit={handleSubmit}>
            <header className="cat-form__header">
                <Link to={`/admin/semen/${semenId}`} className="cat-form__back">Volver al semen</Link>
                <h1 className="cat-form__title">Editar semen</h1>
                <span className="cat-form__subtitle">#{semen.id}</span>
            </header>

            <SemenFields form={form} setField={setField} fieldError={fieldError} disabled={updating} showStatus allowNewSire={false} />

            <footer className="cat-form__footer">
                <Link to={`/admin/semen/${semenId}`} className="cat-form__cancel">Cancelar</Link>
                <Gate domain="semen" action="write">
                    <button type="button" className="cat-form__danger" onClick={handleDelete} disabled={updating || deleting}>
                        {deleting ? 'Eliminando…' : 'Eliminar'}
                    </button>
                </Gate>
                <button type="submit" className="cat-form__submit" disabled={updating || !form.code.trim()}>
                    {updating ? 'Guardando…' : 'Guardar cambios'}
                </button>
            </footer>
        </form>
    );
}
